import { DataNode } from 'components/WorkspaceEditor/types'
import { TrackedChange } from 'models/changes'
import { useCallback, useEffect, useRef } from 'react'
import { useScrollStoreShallowSelector } from '../store/scrollStore'
import { SCROLL_CONFIG } from '../utils/TreeNodesUtils'

/**
 * Custom hook to manage pending scroll requests in a tree structure.
 * It checks if the tree data has changed and attempts to scroll to a node
 * with the latest content if a pending request exists.
 *
 * @param {DataNode[]} treeData - The current tree data.
 * @param {Function} attemptScrollWithCurrentContent - Function to attempt scrolling with the current content.
 */
export const usePendingScrollRequest = (
    treeData: DataNode[],
    attemptScrollWithCurrentContent: (
        scrollToId: string,
        correspondingTrackChange: TrackedChange
    ) => Promise<boolean>
) => {
    const {
        pendingScrollRequest,
        clearPendingScrollRequest,
    } = useScrollStoreShallowSelector((state) => ({
        pendingScrollRequest: state.pendingScrollRequest,
        clearPendingScrollRequest: state.clearPendingScrollRequest,
    }))

    const prevTreeDataRef = useRef(treeData)
    const attemptScrollWithCurrentContentRef = useRef(
        attemptScrollWithCurrentContent
    )
    const clearPendingScrollRequestRef = useRef(clearPendingScrollRequest)
    attemptScrollWithCurrentContentRef.current = attemptScrollWithCurrentContent
    clearPendingScrollRequestRef.current = clearPendingScrollRequest

    const handleScrollAttempt = useCallback(
        async (scrollToId: string, correspondingTrackChange: TrackedChange) => {
            const success = await attemptScrollWithCurrentContentRef.current(
                scrollToId,
                correspondingTrackChange
            )
            if (success) {
                clearPendingScrollRequestRef.current()
            }
        },
        []
    )

    useEffect(() => {
        const hasNewPendingRequest =
            prevTreeDataRef.current !== treeData && pendingScrollRequest
        let timeoutId: NodeJS.Timeout | null = null
        if (hasNewPendingRequest) {
            const {
                scrollToId,
                correspondingTrackChange,
                timestamp,
            } = pendingScrollRequest
            const isRequestFresh =
                Date.now() - timestamp <
                SCROLL_CONFIG.TIMEOUTS.REQUEST_FRESHNESS

            if (isRequestFresh) {
                timeoutId = setTimeout(() => {
                    handleScrollAttempt(scrollToId, correspondingTrackChange)
                }, SCROLL_CONFIG.DELAYS.ATTEMPT_SCROLL_WITH_LATEST_CONTENT)
            } else {
                clearPendingScrollRequestRef.current()
            }

            return () => {
                if (timeoutId) {
                    clearTimeout(timeoutId)
                }
            }
        }
        prevTreeDataRef.current = treeData
    }, [treeData, pendingScrollRequest])
}
